import _extends from '@babel/runtime/helpers/extends';
import React__default, { useId as useId$1, useMemo, useContext, createRef } from 'react';
import { IdsContext } from '../components/IdsProvider/IdsProvider.js';

const idRef = /*#__PURE__*/createRef();
const componentIds = new WeakMap();
function getNextId(ref = idRef) {
  const current = ref.current ?? 0;
  ref.current = current + 1;
  return `id-${current}`;
}
const useIdRef = () => useContext(IdsContext) ?? idRef;
function useId() {
  if (useId$1) return useId$1();
  const ref = useIdRef();
  return useMemo(() => {
    // In strict mode, React renders components twice on client and counter will be incremented twice.
    // So we need to remember the value somewhere to keep the id same between renders
    // Based on https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/ssr/src/SSRProvider.tsx#L94
    const currentOwner = React__default.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentOwner?.current;
    if (currentOwner) {
      const prevComponentValue = componentIds.get(currentOwner);
      if (prevComponentValue == null) {
        // On the first render, and first call to useId, store the id and state in our weak map.
        componentIds.set(currentOwner, {
          id: ref.current ?? 0,
          state: currentOwner.memoizedState
        });
      } else if (currentOwner.memoizedState !== prevComponentValue.state) {
        // On the second render, the memoizedState gets reset by React.
        // Reset the counter, and remove from the weak map so we don't
        // do this for subsequent useId calls.
        ref.current = prevComponentValue.id;
        componentIds.delete(currentOwner);
      }
    }
    return getNextId(ref);
  }, []);
}

/**
 * HOC for adding unique `id` to component.
 */
function withId(key = 'id') {
  return Comp => props => {
    const value = useId();
    const idProp = {
      [key]: value
    };
    return /*#__PURE__*/React__default.createElement(Comp, _extends({}, idProp, props));
  };
}

export { useId as default, withId };
